// links — single source of truth for NEXUS-generated user-facing URLs.
//
// NEXUS-only (clean slate). Every link the platform emits points at the nexus
// domain. No legacy domain, no `.sentry.red` apex, no fallback.
//   - ordinary links: password reset, email verify, invite, binding code,
//     dashboard deep links, report / notification / email CTAs.
//   - tokenized / SDM / NFC `/t/*` links — see buildSdmTapUrl + the NFC tap
//     host helper at the bottom of this file.

export const NEXUS_FRONTEND_BASE = "https://nexus.sentry.red";
export const NEXUS_API_BASE = "https://api.nexus.sentry.red";

/** The frontend base for generated links. Always nexus in production. A local
 *  dev override may be supplied explicitly by the caller (env-gated) — this
 *  default never falls back to location. */
export function generatedFrontendBase(): string {
  return NEXUS_FRONTEND_BASE;
}

/** The API base for generated links (e.g. report download URLs). Always nexus. */
export function generatedApiBase(): string {
  return NEXUS_API_BASE;
}

/** Normalise a relative path to a single leading slash. Rejects absolute /
 *  protocol-relative URLs so a caller can't smuggle an external origin into a
 *  generated link (open-redirect / brand-drift guard). */
function normalizeRelPath(path: string): string {
  const p = (path ?? "").trim();
  if (p === "" || p === "/") return "/";
  // Reject protocol-relative (`//host`) and absolute (`scheme:`) inputs so a
  // caller can't smuggle an external origin into a generated link.
  if (p.startsWith("//") || /^[a-z][a-z0-9+.-]*:/i.test(p)) {
    throw new Error(`generated link path must be relative, got: ${p.slice(0, 64)}`);
  }
  // Single leading slash → joins cleanly with the (no-trailing-slash) base.
  return p.startsWith("/") ? p : "/" + p;
}

/** Build a nexus frontend URL from a relative path. Rejects absolute URLs
 *  unless `allowExternal` is explicitly true (default false). */
export function buildFrontendUrl(path: string, opts?: { allowExternal?: boolean }): string {
  if (opts?.allowExternal && /^https?:\/\//i.test(path)) return path;
  return NEXUS_FRONTEND_BASE + normalizeRelPath(path);
}

/** Build a nexus dashboard URL. `path` is the full path (e.g. `/dashboard` or
 *  `dashboard/reports`). */
export function buildDashboardUrl(path: string): string {
  return buildFrontendUrl(path);
}

/** Build a nexus API URL from a relative path (e.g. `/v1/reports/:id/download`). */
export function buildApiUrl(path: string): string {
  return NEXUS_API_BASE + normalizeRelPath(path);
}

// ── NFC / SDM / tokenized public links (NEXUS rebrand Phase 2 group 9 / #695) ──
//
// Tokenized + NFC tap landing pages (/pair, /scan, /verify, /t/*) are public and
// live on the nexus frontend / api. SDM tap URLs burned onto NTAG424 tags MUST
// point at the nexus API host. The SDM CMAC is computed over (uid + counter)
// with a fixed KDF salt — it does NOT include the URL host — so the burned URL
// is a host-independent crypto contract.

/** Frontend base for tokenized / NFC public landing pages (= nexus). */
export function tokenizedFrontendBase(): string {
  return NEXUS_FRONTEND_BASE;
}

export interface SdmTapUrlParams {
  /** 14-hex UID (uppercase). */
  uid: string;
  /** 16-hex truncated SDM CMAC. */
  sdmmac: string;
  /** SDM read counter (hex string on the wire). */
  sdmcounter: string;
  /** Optional opaque nonce. */
  nonce?: string | null;
  /** Routing slug after /v1/t/. Picker writes the literal "tag" by default. */
  token?: string;
}

/** Build the canonical NTAG424 SDM tap URL on the nexus API host:
 *  `https://api.nexus.sentry.red/v1/t/<token>?uid=…&sdmmac=…&sdmcounter=…[&nonce=…]`.
 *  Single source of truth so any in-repo provisioning / preview / export emits
 *  the nexus host — never the apex. */
export function buildSdmTapUrl(p: SdmTapUrlParams): string {
  const token = (p.token ?? "").trim() || "tag";
  const qs = new URLSearchParams({ uid: p.uid, sdmmac: p.sdmmac, sdmcounter: p.sdmcounter });
  if (p.nonce) qs.set("nonce", p.nonce);
  return buildApiUrl(`/v1/t/${encodeURIComponent(token)}?${qs.toString()}`);
}

/** The ONLY supported NFC tap API host. */
export const NFC_TAP_API_HOST = "api.nexus.sentry.red";

function normalizeNfcHost(host: string | null | undefined): string | null {
  if (!host) return null;
  return host.split(":")[0].trim().toLowerCase();
}

/** True when an `/v1/t/*` request arrived on the ONLY supported NFC tap host.
 *  Any other host (unknown / preview / a stale burned tag) is NOT known and the
 *  caller must fail closed. */
export function isKnownNfcTapHost(host: string | null | undefined): boolean {
  return normalizeNfcHost(host) === NFC_TAP_API_HOST;
}
